var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useStylesHook } from "../hooks/useStylesHook";
import { useThemeContext } from "../theming/useThemeContext";
import { PORTAL_THEME, TMS_THEME } from "../theming/theming";
import { Client } from "../theming/ThemeProvider";
import { BUTTON_BOX_SHADOW, BUTTON_OUTLINE } from "../Button/Button.styles";
import { tone } from "../theming/colors";
import { CONTENT_LABEL } from "../styling/SelectionArea.styles";
import { DISABLED_CURSOR } from "../styling/utils";
var standardLabel = function () { return ({
    paddingLeft: 20,
    cursor: "pointer",
    fontWeight: "normal!important",
    marginBottom: 4,
    display: "inline-block",
    maxWidth: "100%",
}); };
var standardInput = function (brandedBackground) { return ({
    appearance: "none",
    backgroundColor: "#fff",
    margin: [2, 0, 0, 0],
    padding: 0,
    font: "inherit",
    color: TMS_THEME.palette.gray.silver.color,
    width: 14,
    height: 14,
    border: [1, "solid", TMS_THEME.palette.gray.smoke.color],
    borderRadius: "50%",
    transform: "translateY(-0.075em)",
    display: "grid",
    placeContent: "center",
    cursor: "pointer",
    position: "absolute",
    boxShadow: "inset 1px 1px 1px 1px rgba(0, 0, 0, 0.08)",
    boxSizing: "border-box",
    "&:before": {
        content: '""',
        width: 8,
        height: 8,
        borderRadius: "50%",
        transform: "scale(0)",
        transition: "120ms transform ease-in-out",
        boxShadow: "inset 1em 1em ".concat(brandedBackground !== null && brandedBackground !== void 0 ? brandedBackground : TMS_THEME.palette.primary.base.color),
    },
    "&:checked:before": {
        transform: "scale(1)",
    },
    "&:checked": {
        borderColor: brandedBackground !== null && brandedBackground !== void 0 ? brandedBackground : TMS_THEME.palette.primary.base.color,
    },
    "&:disabled": __assign({}, standardInputDisabled()),
    "&:disabled:checked": {
        opacity: 0.7,
    },
    "&:disabled:checked:hover": {
        border: [1, "solid", TMS_THEME.palette.primary.base.color],
    },
    "&:focus": {
        outlineColor: brandedBackground !== null && brandedBackground !== void 0 ? brandedBackground : TMS_THEME.palette.primary.base.color,
        backgroundColor: brandedBackground !== null && brandedBackground !== void 0 ? brandedBackground : TMS_THEME.palette.primary.base.color,
    },
    "&:hover": {
        border: [1, "solid", TMS_THEME.palette.gray.silver.color],
    },
}); };
var standardLabelDisabled = function () { return ({
    cursor: DISABLED_CURSOR,
}); };
var standardInputDisabled = function () { return ({
    "&:hover": { border: [1, "solid", TMS_THEME.palette.gray.smoke.color] },
    backgroundColor: TMS_THEME.palette.gray.fog.color,
    boxShadow: "unset",
}); };
var buttonLabel = function (client, disabled) { return ({
    padding: [client === Client.Portal ? 6 : 5, 10],
    margin: 0,
    marginBottom: "0!important",
    cursor: disabled ? "default" : "pointer",
    fontWeight: TMS_THEME.typography.strong.fontWeight,
}); };
var buttonLabelDisabled = function () { return ({
    cursor: DISABLED_CURSOR,
}); };
var buttonInput = function () { return ({
    position: "absolute",
    clip: "rect(0, 0, 0, 0)",
    pointerEvents: "none",
}); };
var buttonSelectionAreaChecked = function (buttonStyles) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var alternateState = {
        color: (_b = (_a = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.primary) === null || _a === void 0 ? void 0 : _a.color) !== null && _b !== void 0 ? _b : TMS_THEME.palette.primary.dark.contrast,
        backgroundColor: ((_c = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.primary) === null || _c === void 0 ? void 0 : _c.backgroundColor)
            ? tone((_d = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.primary) === null || _d === void 0 ? void 0 : _d.backgroundColor, -15)
            : TMS_THEME.palette.primary.dark.color,
    };
    return {
        backgroundColor: (_f = (_e = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.primary) === null || _e === void 0 ? void 0 : _e.backgroundColor) !== null && _f !== void 0 ? _f : TMS_THEME.palette.primary.base.color,
        color: (_h = (_g = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.primary) === null || _g === void 0 ? void 0 : _g.color) !== null && _h !== void 0 ? _h : TMS_THEME.palette.primary.base.contrast,
        borderColor: (_k = (_j = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.primary) === null || _j === void 0 ? void 0 : _j.color) !== null && _k !== void 0 ? _k : TMS_THEME.palette.primary.dark.color,
        "& + div": {
            borderLeftColor: "rgba(0,0,0,0)",
        },
        "&:hover": alternateState,
    };
};
var buttonSelectionArea = function (first, last, buttonStyles, client) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var alternateState = {
        color: TMS_THEME.palette.gray.slate.color,
        backgroundColor: TMS_THEME.palette.gray.fog.color,
    };
    var button = {
        color: TMS_THEME.palette.gray.slate.color,
        backgroundColor: TMS_THEME.palette.gray.white.color,
        position: "relative",
        float: "left",
        borderLeftWidth: 0,
        borderTopWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: 0,
        borderStyle: "solid",
        borderColor: "rgba(0,0,0,0)",
        verticalAlign: "middle",
        margin: 0,
        borderTopLeftRadius: first ? TMS_THEME.root.borderRadius : 0,
        borderBottomLeftRadius: first ? TMS_THEME.root.borderRadius : 0,
        borderTopRightRadius: last ? TMS_THEME.root.borderRadius : 0,
        borderBottomRightRadius: last ? TMS_THEME.root.borderRadius : 0,
        cursor: "pointer",
        fontSize: TMS_THEME.typography.p.fontSize,
        opacity: 1,
        "&:focus-within": BUTTON_OUTLINE,
        "&:focus": BUTTON_OUTLINE,
        "&:hover": alternateState,
    };
    if (client !== Client.Portal) {
        button = __assign(__assign({}, button), { borderColor: TMS_THEME.palette.gray.smoke.color, borderLeftWidth: 1, borderTopWidth: 1, borderRightWidth: last ? 1 : 0, borderBottomWidth: 1 });
    }
    if (client === Client.Portal) {
        var alternateState_1 = {
            color: TMS_THEME.palette.primary.base.color,
            backgroundColor: TMS_THEME.palette.gray.fog.color,
        };
        button = __assign(__assign({}, button), { margin: 0, borderTopLeftRadius: first
                ? "".concat((_b = (_a = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.default) === null || _a === void 0 ? void 0 : _a.borderRadius) !== null && _b !== void 0 ? _b : PORTAL_THEME.root.borderRadius)
                : 0, borderBottomLeftRadius: first
                ? "".concat((_d = (_c = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.default) === null || _c === void 0 ? void 0 : _c.borderRadius) !== null && _d !== void 0 ? _d : PORTAL_THEME.root.borderRadius)
                : 0, borderTopRightRadius: last
                ? "".concat((_f = (_e = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.default) === null || _e === void 0 ? void 0 : _e.borderRadius) !== null && _f !== void 0 ? _f : PORTAL_THEME.root.borderRadius)
                : 0, borderBottomRightRadius: last
                ? "".concat((_h = (_g = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.default) === null || _g === void 0 ? void 0 : _g.borderRadius) !== null && _h !== void 0 ? _h : PORTAL_THEME.root.borderRadius)
                : 0, fontSize: PORTAL_THEME.typography.p.fontSize, color: TMS_THEME.palette.primary.base.color, "&:hover": alternateState_1 });
    }
    return button;
};
var buttonSelectionAreaDisabled = function (client) {
    var button = {
        opacity: 0.5,
        "&:hover": {
            color: TMS_THEME.palette.gray.slate.color,
            backgroundColor: TMS_THEME.palette.gray.white.color,
        },
    };
    if (client === Client.Portal) {
        button = __assign(__assign({}, button), { "&:hover": {
                backgroundColor: TMS_THEME.palette.gray.fog.color,
                color: TMS_THEME.palette.primary.base.color,
            } });
    }
    return button;
};
var buttonGroup = function (client, buttonStyles) {
    var _a, _b;
    var buttonGroup = {
        position: "relative",
        display: "inline-block",
        verticalAlign: "middle",
        boxShadow: "unset",
        borderRadius: "initial",
    };
    if (client === Client.Portal) {
        buttonGroup = __assign(__assign({}, buttonGroup), { boxShadow: BUTTON_BOX_SHADOW, borderRadius: "".concat((_b = (_a = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.default) === null || _a === void 0 ? void 0 : _a.borderRadius) !== null && _b !== void 0 ? _b : PORTAL_THEME.root.borderRadius) });
    }
    return buttonGroup;
};
var buttonRadioButtons = function () { return ({
    display: "block",
    width: "100%",
    height: 32,
}); };
var iconInput = function () { return (__assign(__assign({}, standardInput()), { bottom: 10, left: 0, right: 0, marginLeft: "auto!important", marginRight: "auto!important" })); };
var iconInputDisabled = function () { return ({
    opacity: 0.7,
    cursor: DISABLED_CURSOR,
}); };
var iconLabel = function () { return ({
    flex: 1,
    minHeight: 56,
    fontStyle: "normal",
    textTransform: "uppercase",
    width: "100%",
    cursor: "pointer",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    marginBottom: "0px !important",
}); };
var iconLabelDisabled = function () { return ({
    opacity: 0.7,
    cursor: DISABLED_CURSOR,
}); };
export function useRadioButtonClasses(_a) {
    var _b;
    var type = _a.type, last = _a.last, first = _a.first;
    var _c = useThemeContext(), client = _c.client, buttonStyles = _c.buttonStyles;
    var radioButtons = {};
    var radioButtonsGroup = {};
    var input = {};
    var inputDisabled = {};
    var label = {};
    var labelDisabled = {};
    var buttonArea = {};
    var buttonAreaChecked = {};
    var buttonAreaDisabled = {};
    var buttonAreaCheckedDisabled = {};
    var richBlock = {};
    var richLabel = {};
    var richIcon = {};
    if (type === "standard") {
        label = standardLabel();
        input = standardInput((_b = buttonStyles === null || buttonStyles === void 0 ? void 0 : buttonStyles.primary) === null || _b === void 0 ? void 0 : _b.backgroundColor);
        inputDisabled = standardInputDisabled();
        labelDisabled = standardLabelDisabled();
    }
    if (type === "button") {
        label = buttonLabel(client);
        labelDisabled = buttonLabelDisabled();
        input = buttonInput();
        buttonArea = buttonSelectionArea(first, last, buttonStyles, client);
        buttonAreaDisabled = buttonSelectionAreaDisabled();
        buttonAreaChecked = buttonSelectionAreaChecked(buttonStyles);
        buttonAreaCheckedDisabled = buttonSelectionAreaChecked(buttonStyles);
        radioButtonsGroup = buttonGroup(client, buttonStyles);
        radioButtons = buttonRadioButtons();
    }
    if (type === "icon") {
        inputDisabled = iconInputDisabled();
        labelDisabled = iconLabelDisabled();
        input = iconInput();
        label = iconLabel();
        richLabel = __assign(__assign({}, CONTENT_LABEL), { flex: 1, textAlign: "center", paddingTop: 4 });
        richIcon = { flex: 1 };
        richBlock = {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minHeight: 60,
            padding: [10, 10, 0, 10],
            width: "100%",
        };
    }
    return useStylesHook({
        radioButtons: radioButtons,
        radioButtonsGroup: radioButtonsGroup,
        input: input,
        label: label,
        buttonArea: buttonArea,
        buttonAreaChecked: buttonAreaChecked,
        richLabel: richLabel,
        richIcon: richIcon,
        richBlock: richBlock,
        inputDisabled: inputDisabled,
        labelDisabled: labelDisabled,
        buttonAreaDisabled: buttonAreaDisabled,
        buttonAreaCheckedDisabled: buttonAreaCheckedDisabled,
    });
}
